import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  Check, ExternalLink, Link2, Loader2, Pencil, Plus,
  Trash2, X, Hotel, Plane, MapPin, Star, Building2, Car, Home,
} from 'lucide-react'
import { addLink, deleteLink, listLinks, updateLink } from '../../shared/api/links'
import type { UpdateLinkInput } from '../../shared/api/links.http'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import type { LinkSource, TripLink } from '../../shared/api/types'

// ---------------------------------------------------------------------------
// Source metadata
// ---------------------------------------------------------------------------

const SOURCE_META: Record<LinkSource, { label: string; color: string; Icon: typeof Link2 }> = {
  booking:     { label: 'Booking.com', color: 'text-blue-700 bg-blue-50 border-blue-200',       Icon: Hotel },
  airbnb:      { label: 'Airbnb',      color: 'text-rose-700 bg-rose-50 border-rose-200',       Icon: Home },
  skyscanner:  { label: 'Skyscanner',  color: 'text-cyan-700 bg-cyan-50 border-cyan-200',       Icon: Plane },
  tripadvisor: { label: 'TripAdvisor', color: 'text-green-700 bg-green-50 border-green-200',    Icon: Star },
  hotels:      { label: 'Hotels.com',  color: 'text-orange-700 bg-orange-50 border-orange-200', Icon: Building2 },
  expedia:     { label: 'Expedia',     color: 'text-yellow-700 bg-yellow-50 border-yellow-200', Icon: Plane },
  vrbo:        { label: 'VRBO',        color: 'text-violet-700 bg-violet-50 border-violet-200', Icon: Car },
  generic:     { label: 'Link',        color: 'text-st-muted bg-st-surface-2 border-st-line',   Icon: Link2 },
}

// ---------------------------------------------------------------------------
// Price summary
// ---------------------------------------------------------------------------

function PriceSummary({ links }: { links: TripLink[] }) {
  const priced = links.filter((l) => l.price != null)
  if (priced.length === 0) return null

  // Group by currency
  const byCurrency: Record<string, number> = {}
  for (const l of priced) {
    byCurrency[l.currency] = (byCurrency[l.currency] ?? 0) + (l.price ?? 0)
  }

  return (
    <div className="mb-5 flex flex-wrap items-center gap-3 rounded-st-lg border border-st-line bg-st-surface-2 px-4 py-3">
      <MapPin className="h-4 w-4 shrink-0 text-st-primary" />
      <span className="text-xs font-medium text-st-muted">Saved total:</span>
      {Object.entries(byCurrency).map(([cur, total]) => (
        <span key={cur} className="text-sm font-semibold text-st-text">
          {cur} {total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </span>
      ))}
      <span className="ml-auto text-xs text-st-muted">{priced.length} of {links.length} with price</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Skeleton card
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div className="flex animate-pulse gap-3 rounded-st-lg border border-st-line bg-st-surface p-4">
      <div className="h-16 w-24 shrink-0 rounded-st-md bg-st-surface-2" />
      <div className="flex-1 space-y-2 py-1">
        <div className="h-3 w-3/4 rounded bg-st-surface-2" />
        <div className="h-3 w-1/2 rounded bg-st-surface-2" />
        <div className="h-5 w-20 rounded-full bg-st-surface-2" />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------------------

interface EditModalProps {
  link: TripLink
  onSave: (patch: UpdateLinkInput) => void
  onClose: () => void
  isPending: boolean
}

function EditModal({ link, onSave, onClose, isPending }: EditModalProps) {
  const [title, setTitle] = useState(link.title)
  const [price, setPrice] = useState(link.price != null ? String(link.price) : '')
  const [currency, setCurrency] = useState(link.currency || 'USD')
  const [checkIn, setCheckIn] = useState(link.check_in || '')
  const [checkOut, setCheckOut] = useState(link.check_out || '')
  const backdropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    onSave({
      title: title.trim() || link.title,
      price: price === '' ? null : Number(price),
      currency,
      check_in: checkIn || null,
      check_out: checkOut || null,
    })
  }

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      onClick={(e) => e.target === backdropRef.current && onClose()}
    >
      <div className="w-full max-w-md rounded-st-xl border border-st-line bg-st-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-st-line px-5 py-4">
          <h2 className="font-semibold text-st-text">Edit link</h2>
          <button type="button" onClick={onClose} className="rounded-st-sm p-1 text-st-muted hover:bg-st-surface-2">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-st-muted">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text focus:border-st-primary focus:outline-none"
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-st-muted">Price</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="–"
                className="w-full rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text focus:border-st-primary focus:outline-none"
              />
            </div>
            <div className="w-24">
              <label className="mb-1 block text-xs font-medium text-st-muted">Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-st-md border border-st-line bg-st-surface px-2 py-2 text-sm text-st-text focus:border-st-primary focus:outline-none"
              >
                {['USD', 'EUR', 'ILS', 'GBP', 'JPY', 'THB', 'AUD'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-st-muted">Check-in</label>
              <input
                type="date"
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
                className="w-full rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text focus:border-st-primary focus:outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-st-muted">Check-out</label>
              <input
                type="date"
                value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
                className="w-full rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text focus:border-st-primary focus:outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="accent" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Link card
// ---------------------------------------------------------------------------

interface LinkCardProps {
  link: TripLink
  onEdit: () => void
  onDelete: () => void
}

function LinkCard({ link, onEdit, onDelete }: LinkCardProps) {
  const meta = SOURCE_META[link.source] ?? SOURCE_META.generic
  const { Icon } = meta
  const nightCount =
    link.check_in && link.check_out
      ? Math.round(
          (new Date(link.check_out).getTime() - new Date(link.check_in).getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : null

  return (
    <div className="group flex gap-3 rounded-st-lg border border-st-line bg-st-surface p-4 hover:border-st-primary/30 transition-colors">
      {link.image_url ? (
        <img
          src={link.image_url}
          alt=""
          className="h-20 w-28 shrink-0 rounded-st-md object-cover"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
        />
      ) : (
        <div className="flex h-20 w-28 shrink-0 items-center justify-center rounded-st-md bg-st-surface-2 text-st-muted">
          <Icon className="h-7 w-7" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        {/* Title + actions */}
        <div className="flex items-start justify-between gap-2">
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold text-st-text hover:text-st-primary hover:underline"
          >
            <span className="line-clamp-2">{link.title || link.url}</span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
          </a>
          <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              onClick={onEdit}
              className="rounded-st-sm p-1.5 text-st-muted hover:bg-st-surface-2 hover:text-st-text"
              aria-label="Edit link"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded-st-sm p-1.5 text-st-muted hover:bg-red-50 hover:text-red-600"
              aria-label="Remove link"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {link.description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-st-muted">{link.description}</p>
        )}

        {/* Badges */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${meta.color}`}
          >
            {meta.label}
          </span>

          {link.price != null && (
            <span className="text-xs font-semibold text-st-text">
              {link.currency} {link.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          )}

          {link.check_in && (
            <span className="text-xs text-st-muted">
              {link.check_in}
              {link.check_out ? ` → ${link.check_out}` : ''}
              {nightCount && nightCount > 0 ? ` (${nightCount}n)` : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function LinksTab() {
  const { id: tripId } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const [url, setUrl] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [editingLink, setEditingLink] = useState<TripLink | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data: links = [], isLoading } = useQuery({
    queryKey: ['links', tripId],
    queryFn: () => listLinks(tripId!),
    enabled: Boolean(tripId),
  })

  const addMutation = useMutation({
    mutationFn: (u: string) => addLink(tripId!, u),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['links', tripId] })
      setUrl('')
      setAddError(null)
    },
    onError: () => setAddError('Could not fetch that URL — check it and try again.'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateLinkInput }) =>
      updateLink(tripId!, id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['links', tripId] })
      setEditingLink(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) => deleteLink(tripId!, linkId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['links', tripId] }),
  })

  // Paste from clipboard
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (document.activeElement !== inputRef.current) return
      const text = e.clipboardData?.getData('text/plain') ?? ''
      if (/^https?:\/\//i.test(text)) setUrl(text)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [])

  function handleAdd(e: FormEvent) {
    e.preventDefault()
    const trimmed = url.trim()
    if (!trimmed) return
    setAddError(null)
    addMutation.mutate(trimmed)
  }

  return (
    <div className="mx-auto max-w-3xl pb-12">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-st-text">Links</h1>
        <p className="text-sm text-st-muted">
          Save Booking, Airbnb, Skyscanner or any travel URL — details are extracted automatically.
        </p>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="mb-5 flex gap-2">
        <input
          ref={inputRef}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.booking.com/hotel/…  (or paste a URL)"
          className="min-w-0 flex-1 rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text placeholder:text-st-muted focus:border-st-primary focus:outline-none"
          disabled={addMutation.isPending}
        />
        <Button type="submit" variant="accent" disabled={!url.trim() || addMutation.isPending}>
          {addMutation.isPending ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Fetching…</>
          ) : (
            <><Plus className="h-4 w-4" /> Add</>
          )}
        </Button>
      </form>

      {addError && <p className="mb-4 text-sm text-red-600">{addError}</p>}

      {/* Price summary */}
      <PriceSummary links={links} />

      {isLoading && <Spinner label="Loading links…" />}

      {/* Skeleton while parsing */}
      {addMutation.isPending && <SkeletonCard />}

      {!isLoading && links.length === 0 && !addMutation.isPending && (
        <EmptyState
          Icon={Link2}
          title="No links yet"
          description="Paste a Booking.com, Airbnb, or any travel URL above."
        />
      )}

      {links.length > 0 && (
        <div className="space-y-3">
          {links.map((link) => (
            <LinkCard
              key={link.id}
              link={link}
              onEdit={() => setEditingLink(link)}
              onDelete={() => deleteMutation.mutate(link.id)}
            />
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editingLink && (
        <EditModal
          link={editingLink}
          onSave={(patch) => updateMutation.mutate({ id: editingLink.id, patch })}
          onClose={() => setEditingLink(null)}
          isPending={updateMutation.isPending}
        />
      )}
    </div>
  )
}
